import { useEffect, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation } from '@tanstack/react-query';
import { getErrorMessage } from '@/lib/platform';

import { passesApi } from '@/lib/passes-api';
import { usePassDetail } from '@/lib/use-passes-booking';
import { formatDateTime, formatMoney } from '@/lib/format';
import { PASS_CANCEL_REASONS, PASS_RATE_CATEGORY } from '@/lib/passes-format';
import type { PassContact, PassTicketPass } from '@/lib/passes-types';
import { ErrorState } from '@/components/shared/error-state';
import { ReportProblemModal, type ReportCategory } from '@/components/shared/report-problem-modal';
import { RateRideModal } from '@/components/rides/rate-ride-modal';
import { Skeleton } from '@/components/shared/skeleton';
import { Screen } from '@/components/chrome';
import { Button } from '@/components/ui/button';
import { StatusBadge } from '@/components/ui/status-badge';
import { Spinner } from '@/components/ui/spinner';
import { QrFrame } from '@/components/ui/qr-frame';
import { Icon } from '@/components/ui/icon';

export const Route = createFileRoute('/_authed/passes/$id')({
  component: PassDetailPage,
});

type CancelStage = 'none' | 'choose' | 'soft' | 'preview' | 'confirm' | 'done';

// TRV14 entry-pass IGM grievance sub-categories — the BAP-raisable, rider-facing subset of the
// "TRV14 - Experiences" IGM Category and Sub Category sheet (14x-series). The backend derives the
// parent category (ORD/ITM/FLM/PMT) from the picked code — never the legacy retail ITEM/ITM001.
const PASSES_ISSUE_CATEGORIES: ReportCategory[] = [
  { code: 'ORD141', label: 'Confirmation voucher not received' },
  { code: 'ORD142', label: 'Incorrect ticket details (name/date/slot)' },
  { code: 'FLM141', label: 'QR code not working' },
  { code: 'FLM143', label: 'Entry denied despite valid ticket' },
  { code: 'FLM144', label: 'Venue closed on arrival' },
  { code: 'ITM142', label: 'Service did not match the listing' },
  { code: 'PMT143', label: 'Cancellation policy not honored' },
  { code: 'PMT144', label: 'Wrong refund received' },
];

function PassDetailPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();

  const [cancelStage, setCancelStage] = useState<CancelStage>('none');
  const [cancelReason, setCancelReason] = useState(PASS_CANCEL_REASONS[0]?.code ?? '001');
  const [cancelLeg, setCancelLeg] = useState<string | undefined>(undefined);
  const [cancelError, setCancelError] = useState('');
  const [reportOpen, setReportOpen] = useState(false);
  const [rateOpen, setRateOpen] = useState(false);
  const [rated, setRated] = useState(false);
  const [contact, setContact] = useState<PassContact | null>(null);

  const awaiting = cancelStage === 'soft' || cancelStage === 'confirm';
  const { ticket, isLoading, isError, error, refetch } = usePassDetail(id, awaiting);

  const cancelMut = useMutation({
    mutationFn: (vars: { stage: 'SOFT' | 'CONFIRM'; reason?: string; fulfillmentId?: string }) =>
      passesApi.cancel(id, vars),
  });
  const rateMut = useMutation({
    mutationFn: (value: string) => passesApi.rate(id, { category: PASS_RATE_CATEGORY, value }),
  });
  const supportMut = useMutation({
    mutationFn: () => passesApi.support(id),
    onSuccess: (c) => setContact(c),
  });

  // Advance the 2-stage cancel as the order status lands via polling.
  useEffect(() => {
    if (!ticket) return;
    if (cancelStage === 'soft' && ticket.status === 'SOFT_CANCEL' && ticket.cancellation) {
      setCancelStage('preview');
    }
    if (cancelStage === 'confirm' && ticket.status === 'CANCELLED') {
      setCancelStage('done');
    }
  }, [ticket, cancelStage]);

  // Don't spin forever if the operator's cancel callback never lands (Beckn TTL PT30S).
  useEffect(() => {
    if (cancelStage !== 'soft' && cancelStage !== 'confirm') return;
    const timer = setTimeout(() => {
      setCancelError(
        cancelStage === 'soft'
          ? 'The venue is taking longer than expected to calculate your refund. Please try again.'
          : 'Cancellation is taking longer than expected. Check My Trips shortly, or try again.'
      );
      setCancelStage((s) => (s === 'soft' ? 'none' : 'preview'));
    }, 35_000);
    return () => clearTimeout(timer);
  }, [cancelStage]);

  function chooseCancel(fulfillmentId?: string) {
    setCancelError('');
    setCancelLeg(fulfillmentId);
    setCancelStage('choose');
  }

  function startCancel() {
    setCancelError('');
    setCancelStage('soft');
    cancelMut.mutate(
      { stage: 'SOFT', reason: cancelReason, fulfillmentId: cancelLeg },
      {
        onError: (e) => {
          setCancelError(cancelErrorMessage(e));
          setCancelStage('none');
        },
      }
    );
  }

  function confirmCancel() {
    setCancelStage('confirm');
    cancelMut.mutate(
      { stage: 'CONFIRM', reason: cancelReason, fulfillmentId: cancelLeg },
      {
        onError: (e) => {
          setCancelError(cancelErrorMessage(e));
          setCancelStage('preview');
        },
      }
    );
  }

  const back = () => void navigate({ to: '/trips' });

  if (isLoading) {
    return (
      <Screen title="Pass" onBack={back} accent="pass">
        <div className="wb-col wb-gap-16">
          <Skeleton w="60%" h={24} />
          <Skeleton w="100%" h={280} r={22} />
        </div>
      </Screen>
    );
  }
  if (isError || !ticket) {
    return (
      <Screen title="Pass" onBack={back} accent="pass">
        <ErrorState error={error} onRetry={() => void refetch()} />
      </Screen>
    );
  }

  const passes = ticket.passes ?? [];
  const title = ticket.name || ticket.provider?.name || 'Entry pass';
  const canRate = ticket.status === 'COMPLETED' && !rated;

  return (
    <Screen title="Pass" onBack={back} accent="pass">
      <div className="wb-col wb-gap-16">
        <div className="wb-row wb-between wb-gap-12" style={{ alignItems: 'flex-start' }}>
          <div className="wb-col wb-gap-4">
            <span className="wb-eyebrow">Entry pass</span>
            <h1 className="wb-h2">{title}</h1>
            {ticket.provider?.name && title !== ticket.provider.name && (
              <p className="wb-sub">{ticket.provider.name}</p>
            )}
          </div>
          <StatusBadge status={ticket.status} category="pass" />
        </div>

        {/* QR passes (one credential per fulfillment) */}
        <div className="wb-col wb-gap-16">
          {passes.map((pass, i) => (
            <PassCard
              key={pass.fulfillmentId || i}
              pass={pass}
              multi={passes.length > 1}
              index={i}
            />
          ))}
        </div>

        {/* Payment summary */}
        <div className="wb-card wb-card--pad wb-small">
          Paid {formatMoney(ticket.payment?.amount)} · {ticket.payment?.status} ·{' '}
          {formatDateTime(ticket.purchasedAt)}
        </div>

        {/* Cancellation preview / outcome */}
        {ticket.cancellation && cancelStage !== 'soft' && (
          <div
            className="wb-card wb-card--pad wb-col wb-gap-4"
            style={{ background: 'var(--honey-wash-2)' }}
          >
            <p className="wb-strong">
              {ticket.status === 'CANCELLED' ? 'Cancelled' : 'Refund preview'}
            </p>
            {ticket.cancellation.refund && (
              <p className="wb-small">Refund: {formatMoney(ticket.cancellation.refund)}</p>
            )}
            {ticket.cancellation.charges && (
              <p className="wb-small">Charges: {formatMoney(ticket.cancellation.charges)}</p>
            )}
          </div>
        )}

        {/* Contact (support) */}
        {contact && (contact.phone || contact.email || contact.url) && (
          <div className="wb-card wb-card--pad wb-col wb-gap-4 wb-small">
            <p className="wb-strong">Venue contact</p>
            {contact.phone && (
              <a href={`tel:${contact.phone}`} className="wb-row wb-gap-8 wb-ink2">
                <Icon name="phone" size={16} /> {contact.phone}
              </a>
            )}
            {contact.email && (
              <a href={`mailto:${contact.email}`} className="wb-row wb-gap-8 wb-ink2">
                <Icon name="message" size={16} /> {contact.email}
              </a>
            )}
            {contact.url && (
              <a
                href={contact.url}
                target="_blank"
                rel="noopener noreferrer"
                className="wb-row wb-gap-8"
                style={{
                  color: 'var(--honey-700)',
                  textDecoration: 'underline',
                  overflow: 'hidden',
                }}
              >
                <Icon name="arrowRight" size={16} /> {contact.url}
              </a>
            )}
          </div>
        )}

        {cancelError && (
          <p className="wb-small" style={{ color: 'var(--danger)' }}>
            {cancelError}
          </p>
        )}

        {/* Reason chooser (before SOFT) */}
        {cancelStage === 'choose' && (
          <div className="wb-card wb-card--pad wb-col wb-gap-12">
            <p className="wb-strong">Why are you cancelling?</p>
            <div className="wb-col wb-gap-8">
              {PASS_CANCEL_REASONS.map((r) => (
                <label
                  key={r.code}
                  className={`wb-option ${cancelReason === r.code ? 'is-selected' : ''}`}
                  style={{ cursor: 'pointer' }}
                >
                  <span className="wb-radio-dot" />
                  <input
                    type="radio"
                    name="cancel-reason"
                    checked={cancelReason === r.code}
                    onChange={() => setCancelReason(r.code)}
                    style={{ position: 'absolute', opacity: 0 }}
                  />
                  <span className="wb-grow">{r.label}</span>
                </label>
              ))}
            </div>
            <div className="wb-row wb-gap-8">
              <Button variant="ghost" size="sm" onClick={() => setCancelStage('none')}>
                Keep pass
              </Button>
              <Button variant="danger" size="sm" onClick={startCancel}>
                Calculate refund
              </Button>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="wb-row wb-wrap wb-gap-8">
          <Button variant="secondary" size="sm" onClick={() => setReportOpen(true)}>
            Report a problem
          </Button>

          <Button
            variant="secondary"
            size="sm"
            onClick={() => supportMut.mutate()}
            disabled={supportMut.isPending}
          >
            {supportMut.isPending ? 'Getting details…' : 'Show venue contact'}
          </Button>

          {canRate && (
            <Button variant="secondary" size="sm" onClick={() => setRateOpen(true)}>
              Rate your visit
            </Button>
          )}

          {ticket.cancellable && cancelStage === 'none' && (
            <Button variant="danger" size="sm" onClick={() => chooseCancel()}>
              Cancel pass
            </Button>
          )}

          {(cancelStage === 'soft' || cancelStage === 'confirm') && (
            <span className="wb-row wb-gap-8 wb-small">
              <Spinner />
              {cancelStage === 'soft' ? 'Calculating refund…' : 'Cancelling…'}
            </span>
          )}

          {cancelStage === 'preview' && (
            <Button variant="danger" size="sm" onClick={confirmCancel}>
              Confirm cancellation
            </Button>
          )}
        </div>

        {/* Per-pass cancel when there are multiple credentials */}
        {passes.length > 1 && ticket.cancellable && cancelStage === 'none' && (
          <div className="wb-col wb-gap-8">
            <p className="wb-tiny">Cancel a single pass</p>
            <div className="wb-row wb-wrap wb-gap-8">
              {passes.map((p, i) => (
                <Button
                  key={p.fulfillmentId}
                  variant="secondary"
                  size="sm"
                  onClick={() => chooseCancel(p.fulfillmentId)}
                >
                  Cancel pass {i + 1}
                </Button>
              ))}
            </div>
          </div>
        )}

        {reportOpen && (
          <ReportProblemModal
            placeholder="Describe the issue with your entry pass…"
            categories={PASSES_ISSUE_CATEGORIES}
            onClose={() => setReportOpen(false)}
            onSubmit={async (description, extras) => {
              const ack = await passesApi.raiseIssue(id, {
                description,
                subCategory: extras.subCategory,
              });
              setReportOpen(false);
              void navigate({
                to: '/support/$category/$issueId',
                params: { category: 'passes', issueId: ack.issueId },
              });
            }}
          />
        )}

        {rateOpen && (
          <RateRideModal
            title="Rate your visit"
            onClose={() => setRateOpen(false)}
            onSubmit={async (value) => {
              await rateMut.mutateAsync(value);
              setRated(true);
              setRateOpen(false);
            }}
          />
        )}
      </div>
    </Screen>
  );
}

// A 409 (ONDC_012 "invalid state transition") means the order is already mid-cancel/cancelled.
function cancelErrorMessage(e: unknown): string {
  const err = e as { status?: number; code?: string } | null;
  if (err?.status === 409 || err?.code === 'ONDC_012') {
    return "This pass can't be cancelled right now — it may already be cancelling or cancelled. Refresh for the latest status.";
  }
  return getErrorMessage(e);
}

function PassCard({ pass, multi, index }: { pass: PassTicketPass; multi: boolean; index: number }) {
  return (
    <div
      className="wb-card wb-card--pad wb-col wb-center wb-gap-12"
      style={{ textAlign: 'center' }}
    >
      {multi && <p className="wb-tiny wb-strong">Pass {index + 1}</p>}
      <div style={{ width: '100%', maxWidth: 280 }}>
        {pass.qrImage ? (
          <QrFrame>
            <img src={pass.qrImage} alt="Entry pass QR" style={{ width: '100%', height: 'auto' }} />
          </QrFrame>
        ) : (
          <div className="wb-inset wb-center" style={{ height: 200, color: 'var(--ink-3)' }}>
            <Icon name="qr" size={40} />
          </div>
        )}
      </div>
      <div className="wb-row wb-wrap wb-center wb-gap-8">
        {pass.visitAt && <span className="wb-tiny">Visit {formatDateTime(pass.visitAt)}</span>}
        {pass.validUntil && (
          <span className="wb-tiny">Valid until {formatDateTime(pass.validUntil)}</span>
        )}
        {pass.ticketStatus && <StatusBadge kind="credential" status={pass.ticketStatus} />}
      </div>
    </div>
  );
}
